import { tenantApiRequest } from '../tenant-api-request'
import type {
  CreateRoleDto,
  RoleDetail,
  RoleListData,
  RoleListParams,
  RoleModuleNode,
  UpdateRoleArgs,
} from './roles.types'

/** Drop `undefined` / empty params so we don't send `searchName=` etc. */
function buildListParams(params: RoleListParams): Record<string, string | number> {
  const query: Record<string, string | number> = {
    isActive: params.isActive,
    pageNumber: params.pageNumber,
  }

  if (typeof params.limit === 'number') query.limit = params.limit
  if (params.searchName?.trim()) query.searchName = params.searchName.trim()
  if (params.sortOrder) query.sortOrder = params.sortOrder
  if (params.sortBy) query.sortBy = params.sortBy

  return query
}

/**
 * Roles API — tenant-admin service.
 *
 * Endpoint paths are relative to `VITE_API_URL`
 * (`https://api-dev.akashic.dhira.io/tenant-admin/api/v1`).
 */
export const rolesService = {
  /** `GET /roles` — paginated list of active (`isActive: '1'`) or deleted (`'0'`) roles. */
  fetchRoles: (params: RoleListParams): Promise<RoleListData> =>
    tenantApiRequest<RoleListData>(
      { method: 'GET', url: '/roles', params: buildListParams(params) },
      'Failed to load roles'
    ),

  /** `GET /roles/{roleId}` — full role detail incl. the named `role_feature_access` tree. */
  fetchRoleDetail: (roleId: number): Promise<RoleDetail> =>
    tenantApiRequest<RoleDetail>(
      { method: 'GET', url: `/roles/${roleId}` },
      'Failed to load role details'
    ),

  /** `GET /roles/modules` — feature dependency graph (IDs only, no names). */
  fetchRoleModules: (): Promise<RoleModuleNode[]> =>
    tenantApiRequest<RoleModuleNode[]>(
      { method: 'GET', url: '/roles/modules' },
      'Failed to load role modules'
    ),

  /** `POST /roles` — create a custom role. */
  createRole: (body: CreateRoleDto): Promise<RoleDetail> =>
    tenantApiRequest<RoleDetail>(
      { method: 'POST', url: '/roles', data: body },
      'Failed to create role'
    ),

  /** `PATCH /roles/{roleId}` — update a role's title / description / access. */
  updateRole: ({ roleId, body }: UpdateRoleArgs): Promise<RoleDetail> =>
    tenantApiRequest<RoleDetail>(
      { method: 'PATCH', url: `/roles/${roleId}`, data: body },
      'Failed to update role'
    ),

  /** `DELETE /roles/{roleId}` — soft-delete a role (moves it to the Deleted tab). */
  deleteRole: (roleId: number): Promise<unknown> =>
    tenantApiRequest<unknown>(
      { method: 'DELETE', url: `/roles/${roleId}` },
      'Failed to delete role'
    ),

  /** `POST /roles/{roleId}/duplicate` — clone an existing role. */
  duplicateRole: (roleId: number): Promise<RoleDetail> =>
    tenantApiRequest<RoleDetail>(
      { method: 'POST', url: `/roles/${roleId}/duplicate` },
      'Failed to duplicate role'
    ),

  /** `POST /roles/{roleId}/restore` — restore a deleted role back to active. */
  restoreRole: (roleId: number): Promise<unknown> =>
    tenantApiRequest<unknown>(
      { method: 'POST', url: `/roles/${roleId}/restore` },
      'Failed to restore role'
    ),
}
